import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * VIB-800: Datagrunnlag for T&M-faktura. Henter ufakturerte timer (gruppert per
 * ansatt × hourly_sales_rate) og ufakturerte bilag for et hourly-prosjekt.
 * Markup/include per bilag er UI-state i preview-dialogen, ikke her.
 *
 * VIB-994: parametrisert med valgfri `upsellId` — når satt scopes hooken til
 * ETT T&M-mersalg på et ellers fastpris-prosjekt i stedet for hele
 * prosjektet: timer filtreres på `budget_line_id IN (mersalgets linjer)`, og
 * bilag hentes via `voucher_allocations` mot de samme linjene (allokert
 * BELØP, ikke hele bilagets kost — ett bilag kan være delt over flere
 * linjer/prosjekter). Krever `status='approved'` på mersalget (samme gate som
 * fastpris-fakturering, VIB-665) — et draft T&M-mersalg gir `blocked: true`
 * og tomt datagrunnlag.
 */

export interface TMTimeLine {
  userId: string;
  userName: string;
  hours: number;
  rate: number | null;
  total: number;
  /** ansatt mangler hourly_sales_rate → blokkerer faktura */
  missingRate: boolean;
}

export interface TMVoucher {
  id: string;
  supplierName: string;
  description: string | null;
  cost: number;
}

/**
 * VIB-1185: kun ekte kostnadsbilag kan viderefaktureres i T&M. PowerOffice
 * legger også igjen øreavrundinger (OutgoingCreditNote ~0,50 kr), lønn
 * (PayrollJournal), reverseringer og kreditnotaer på prosjekter — disse er
 * IKKE viderefakturerbare kostnader og skal aldri foreslås på en T&M-faktura.
 */
export const TM_BILLABLE_VOUCHER_TYPES = [
  "IncomingInvoice",
  "TravelExpense",
  "Expense",
] as const;

/** Én rad per ufakturert føring — til timeliste-PDF-vedlegget. */
export interface TMTimesheetRow {
  date: string | null;
  userName: string;
  hours: number;
  comment: string | null;
}

export interface TMInvoiceData {
  timeLines: TMTimeLine[];
  vouchers: TMVoucher[];
  defaultMarkupPct: number;
  /** sum timer-linjer (eks. mva) — bilag legges til i preview etter markup */
  timeTotal: number;
  /** alle ufakturerte time_entry-IDer (markeres fakturert ved opprettelse) */
  timeEntryIds: string[];
  /** rader til timeliste-PDF (én per føring) */
  timesheet: TMTimesheetRow[];
  hasUninvoiced: boolean;
  /** navn på ansatte uten timesats (blokkerer faktura) */
  blockingUsers: string[];
  /** VIB-994: satt (og resten tomt) når scopet mersalg finnes men ikke er godkjent ennå. */
  blocked: boolean;
  blockedReason: "not_approved" | null;
}

/** Én allokering av et bilag mot en budsjettlinje (voucher_allocations-raden). */
export interface TMVoucherAllocationInput {
  voucher_id: string;
  budget_line_id: string | null;
  amount: number | null;
}

export interface TMAllocatedVoucherInput {
  id: string;
  supplier_name: string | null;
  description: string | null;
  status: string | null;
  voucher_type: string;
  invoiced_invoice_id: string | null;
}

/**
 * VIB-994: bygg T&M-fakturerbare bilag-linjer for ETT mersalg fra
 * voucher_allocations (i stedet for hele `vouchers`-tabellen som i
 * prosjekt-bred T&M-fakturering). Ett bilag kan være allokert mot flere av
 * mersalgets linjer — summer allokeringene per bilag så det blir én rad per
 * bilag på fakturaen, akkurat som i prosjekt-bred-modusen.
 *
 * Filter (alle må være sanne):
 * - allokeringen peker på en av `lineIds`
 * - bilaget er `approved` (samme regel som `groupAllocationsByBudgetLine`)
 * - bilaget er av en viderefakturerbar type (`TM_BILLABLE_VOUCHER_TYPES`)
 * - bilaget er ikke allerede fakturert (`invoiced_invoice_id` er null)
 */
export interface TMProjectVoucherInput {
  id: string;
  supplier_name: string | null;
  description: string | null;
  amount: number | null;
  status: string;
}

/**
 * VIB-2929: prosjektbred T&M-viderefakturering. Samme statusregel som den
 * mersalg-scopede varianten: bare `approved` (bokført) kan viderefaktureres.
 * Bilag som venter på godkjenning, er hos daglig leder eller er avvist tilbys
 * ikke. Type og «ikke fakturert» filtreres allerede i spørringen.
 */
export function computeTMVouchersProjectWide(rows: TMProjectVoucherInput[]): TMVoucher[] {
  return rows
    .filter((v) => v.status === "approved")
    .map((v) => ({
      id: v.id,
      supplierName: v.supplier_name ?? "(ukjent leverandør)",
      description: v.description ?? null,
      cost: v.amount ?? 0,
    }));
}

export function computeTMVouchersFromAllocations(
  allocations: TMVoucherAllocationInput[],
  vouchersById: Map<string, TMAllocatedVoucherInput>,
  lineIds: Set<string>,
): TMVoucher[] {
  const billableTypes = new Set<string>(TM_BILLABLE_VOUCHER_TYPES);
  const sums = new Map<string, number>();
  for (const a of allocations) {
    if (!a.budget_line_id || !lineIds.has(a.budget_line_id)) continue;
    const voucher = vouchersById.get(a.voucher_id);
    if (!voucher) continue;
    if (voucher.status !== "approved") continue;
    if (!billableTypes.has(voucher.voucher_type)) continue;
    if (voucher.invoiced_invoice_id) continue;
    sums.set(a.voucher_id, (sums.get(a.voucher_id) ?? 0) + Number(a.amount ?? 0));
  }

  return Array.from(sums.entries())
    .filter(([, cost]) => cost > 0)
    .map(([voucherId, cost]) => {
      const v = vouchersById.get(voucherId)!;
      return {
        id: voucherId,
        supplierName: v.supplier_name ?? "(ukjent leverandør)",
        description: v.description ?? null,
        cost: Math.round(cost * 100) / 100,
      };
    });
}

interface TimeEntryInput {
  user_id: string;
  duration_seconds: number | null;
  manual_hours_override: number | null;
  user_name: string | null;
  hourly_sales_rate: number | null;
}

/** Ren aggregering: ufakturerte timer → én linje per ansatt med timer × sats. */
export function computeTMTimeLines(entries: TimeEntryInput[]): TMTimeLine[] {
  const byUser = new Map<string, { name: string; rate: number | null; hours: number }>();
  for (const e of entries) {
    const hrs = e.manual_hours_override ?? (e.duration_seconds ?? 0) / 3600;
    const cur = byUser.get(e.user_id) ?? {
      name: e.user_name ?? "(ukjent)",
      rate: e.hourly_sales_rate ?? null,
      hours: 0,
    };
    cur.hours += hrs;
    byUser.set(e.user_id, cur);
  }

  return Array.from(byUser.entries())
    .map(([userId, v]) => {
      const hours = Math.round(v.hours * 100) / 100;
      const rate = v.rate;
      return {
        userId,
        userName: v.name,
        hours,
        rate,
        total: rate != null ? Math.round(hours * rate * 100) / 100 : 0,
        missingRate: rate == null,
      };
    })
    .filter((l) => l.hours > 0)
    .sort((a, b) => a.userName.localeCompare(b.userName, "nb"));
}

/**
 * VIB-1183: total per ansatt-linje med sats redigert direkte i fakturadialogen.
 * `rates` er dialogens lokale state (kr/t per userId; "" = ikke fylt inn).
 * Linjer med timer > 0 og manglende/0 sats listes i `missingRateUserIds` og
 * blokkerer opprettelse.
 */
export function computeTMTimeTotals(
  lines: { userId: string; hours: number }[],
  rates: Record<string, number | "">,
): { lineTotals: Record<string, number>; timeTotal: number; missingRateUserIds: string[] } {
  const lineTotals: Record<string, number> = {};
  const missingRateUserIds: string[] = [];
  let timeTotal = 0;
  for (const l of lines) {
    const r = rates[l.userId];
    const hasRate = typeof r === "number" && r > 0;
    if (l.hours > 0 && !hasRate) missingRateUserIds.push(l.userId);
    const total = Math.round(l.hours * (hasRate ? r : 0) * 100) / 100;
    lineTotals[l.userId] = total;
    timeTotal += total;
  }
  return { lineTotals, timeTotal: Math.round(timeTotal * 100) / 100, missingRateUserIds };
}

/** PostgREST kan returnere embeddede relasjoner som objekt eller ett-elements array. */
function embed<T>(rel: unknown, key: string): T | null {
  if (!rel) return null;
  const obj = Array.isArray(rel) ? rel[0] : rel;
  return (obj?.[key] ?? null) as T | null;
}

const EMPTY_TM_DATA: TMInvoiceData = {
  timeLines: [],
  vouchers: [],
  defaultMarkupPct: 30,
  timeTotal: 0,
  timeEntryIds: [],
  timesheet: [],
  hasUninvoiced: false,
  blockingUsers: [],
  blocked: true,
  blockedReason: "not_approved",
};

export function useTMInvoiceData(
  projectId: string | undefined,
  options?: { upsellId?: string },
) {
  const upsellId = options?.upsellId;
  return useQuery({
    queryKey: ["tm-invoice-data", projectId, upsellId ?? null],
    enabled: !!projectId,
    queryFn: async (): Promise<TMInvoiceData> => {
      // VIB-994: scopet til ett T&M-mersalg. Krever status='approved' (samme
      // gate som fastpris-T&M-fakturering, VIB-665) — draft gir blokkert,
      // tomt datagrunnlag i stedet for å kaste (dialogen viser en forklaring).
      let lineIds: string[] | null = null;
      if (upsellId) {
        const { data: upsell, error: upsellErr } = await supabase
          .from("upsells")
          .select("status")
          .eq("id", upsellId)
          .maybeSingle();
        if (upsellErr) throw upsellErr;
        if (upsell?.status !== "approved") {
          return EMPTY_TM_DATA;
        }
        // .limit(1000): defensivt tak (matcher PostgREST-defaulten eksplisitt) —
        // ett mersalg har i praksis et fåtall linjer, aldri i nærheten av 1000.
        const { data: lines, error: linesErr } = await supabase
          .from("budget_lines")
          .select("id")
          .eq("project_id", projectId!)
          .eq("upsell_id", upsellId)
          .limit(1000);
        if (linesErr) throw linesErr;
        lineIds = (lines ?? []).map((l) => l.id as string);
        if (lineIds.length === 0) {
          return { ...EMPTY_TM_DATA, blocked: false, blockedReason: null };
        }
      }

      // Prosjekt-bredt (hourly-prosjekter): ingen budget_line_id-filter (VIB-800
      // — disse prosjektene har i praksis ingen budsjettlinjer). Scopet til ett
      // mersalg: KUN timer mot mersalgets linjer (bounded .in()-read).
      const [entriesRes, vouchersOrAllocationsRes, configRes] = await Promise.all([
        lineIds
          ? supabase
              .from("time_entries")
              .select("id, user_id, duration_seconds, manual_hours_override, start_date, comment, budget_line_id, users:user_id(full_name, hourly_sales_rate)")
              .eq("project_id", projectId!)
              .is("invoiced_invoice_id", null)
              .is("deleted_at", null)
              .in("budget_line_id", lineIds)
          : supabase
              .from("time_entries")
              .select("id, user_id, duration_seconds, manual_hours_override, start_date, comment, budget_line_id, users:user_id(full_name, hourly_sales_rate)")
              .eq("project_id", projectId!)
              .is("invoiced_invoice_id", null)
              .is("deleted_at", null),
        lineIds
          ? supabase
              .from("voucher_allocations")
              .select("voucher_id, budget_line_id, amount, vouchers(id, supplier_name, description, status, voucher_type, invoiced_invoice_id)")
              .in("budget_line_id", lineIds)
          : supabase
              .from("vouchers")
              .select("id, supplier_name, description, amount, status")
              .eq("project_id", projectId!)
              .eq("status", "approved")
              .is("invoiced_invoice_id", null)
              // VIB-1185: ekskluder øreavrunding/lønn/reversering/kreditnota.
              .in("voucher_type", TM_BILLABLE_VOUCHER_TYPES as unknown as string[]),
        supabase.from("system_config").select("value").eq("key", "voucher_markup_pct_default").maybeSingle(),
      ]);

      if (entriesRes.error) throw entriesRes.error;
      if (vouchersOrAllocationsRes.error) throw vouchersOrAllocationsRes.error;

      const entries: TimeEntryInput[] = (entriesRes.data ?? []).map((row: Record<string, unknown>) => ({
        user_id: row.user_id as string,
        duration_seconds: (row.duration_seconds as number | null) ?? null,
        manual_hours_override: (row.manual_hours_override as number | null) ?? null,
        user_name: embed<string>(row.users, "full_name"),
        hourly_sales_rate: embed<number>(row.users, "hourly_sales_rate"),
      }));

      const timeLines = computeTMTimeLines(entries);

      let vouchers: TMVoucher[];
      if (lineIds) {
        // VIB-994: allokerings-scopet — grupper allokeringer per bilag.
        type AllocationRow = {
          voucher_id: string;
          budget_line_id: string | null;
          amount: number | string | null;
          vouchers: TMAllocatedVoucherInput | TMAllocatedVoucherInput[] | null;
        };
        const rows = (vouchersOrAllocationsRes.data ?? []) as unknown as AllocationRow[];
        const vouchersById = new Map<string, TMAllocatedVoucherInput>();
        for (const r of rows) {
          const v = Array.isArray(r.vouchers) ? r.vouchers[0] : r.vouchers;
          if (v) vouchersById.set(r.voucher_id, v);
        }
        const allocations: TMVoucherAllocationInput[] = rows.map((r) => ({
          voucher_id: r.voucher_id,
          budget_line_id: r.budget_line_id,
          amount: r.amount != null ? Number(r.amount) : null,
        }));
        vouchers = computeTMVouchersFromAllocations(allocations, vouchersById, new Set(lineIds));
      } else {
        vouchers = computeTMVouchersProjectWide((vouchersOrAllocationsRes.data ?? []) as TMProjectVoucherInput[]);
      }

      const rawMarkup = configRes.data?.value;
      const defaultMarkupPct = typeof rawMarkup === "number" ? rawMarkup : 30;

      const timeTotal = timeLines.reduce((s, l) => s + l.total, 0);

      const timeEntryIds = (entriesRes.data ?? [])
        .map((row: Record<string, unknown>) => row.id as string)
        .filter(Boolean);

      const timesheet: TMTimesheetRow[] = (entriesRes.data ?? []).map((row: Record<string, unknown>) => ({
        date: (row.start_date as string | null) ?? null,
        userName: embed<string>(row.users, "full_name") ?? "(ukjent)",
        hours:
          (row.manual_hours_override as number | null) ??
          ((row.duration_seconds as number | null) ?? 0) / 3600,
        comment: (row.comment as string | null) ?? null,
      }));

      return {
        timeLines,
        vouchers,
        defaultMarkupPct,
        timeTotal,
        timeEntryIds,
        timesheet,
        hasUninvoiced: timeLines.length > 0 || vouchers.length > 0,
        blockingUsers: timeLines.filter((l) => l.missingRate).map((l) => l.userName),
        blocked: false,
        blockedReason: null,
      };
    },
    staleTime: 60_000,
  });
}
